import { __awaiter } from "tslib";
import { add, addMonths, differenceInMinutes, format } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import processClasses from "./ProcessClasses";
export const encodedLabelsToProcessElement = processClasses.reduce((acc, processClass) => {
    acc[processClass.encodedLabel] = processClass.processElementLabel;
    return acc;
}, {});
const createHasEventsOrHasChildrenWithEvents = (allEvents) => {
    const resourceIdsWithEvents = allEvents.reduce((acc, event) => {
        acc.add(event.resourceId);
        return acc;
    }, new Set());
    const hasEventsOrHasChildrenWithEvents = (resourceItem) => {
        const children = resourceItem.children || [];
        if (children.length === 0) {
            return resourceIdsWithEvents.has(resourceItem.id);
        }
        return (resourceIdsWithEvents.has(resourceItem.id) ||
            children.some((child) => hasEventsOrHasChildrenWithEvents(child)));
    };
    return hasEventsOrHasChildrenWithEvents;
};
export default {
    methods: {
        createSchedulerProData({ planner_items: plannerItems, planned_events: plannedEvents, actual_events: actualEvents, planner_comments: plannerComments, }, now) {
            const parentIdToChildren = {};
            for (const plannerItem of plannerItems.filter(({ parent_id: parentId }) => parentId)) {
                if (parentIdToChildren[plannerItem.parent_id]) {
                    parentIdToChildren[plannerItem.parent_id].push(plannerItem);
                }
                else {
                    parentIdToChildren[plannerItem.parent_id] = [plannerItem];
                }
            }
            const plannerCommentIdBySourceId = plannerComments.reduce((acc, plannerComment) => {
                if (acc[plannerComment.source_id]) {
                    acc[plannerComment.source_id].push(plannerComment);
                }
                else {
                    acc[plannerComment.source_id] = [plannerComment];
                }
                return acc;
            }, {});
            const sourceIdToActualEvent = {};
            for (const actualEvent of actualEvents) {
                sourceIdToActualEvent[actualEvent.source_id] = actualEvent;
            }
            const mapResource = (plannerItem) => {
                var _a, _b;
                return ({
                    id: plannerItem._id,
                    name: plannerItem.name,
                    sourceId: plannerItem.source_id,
                    trackingEnabled: plannerItem.tracking_enabled,
                    children: ((_a = parentIdToChildren[plannerItem._id]) === null || _a === void 0 ? void 0 : _a.map((p) => mapResource(p))) || [],
                    expanded: plannerItem.tracking_enabled &&
                        (!((_b = sourceIdToActualEvent[plannerItem.source_id]) === null || _b === void 0 ? void 0 : _b.end) ||
                            !(plannerItem.source_id in sourceIdToActualEvent)),
                    comments: plannerCommentIdBySourceId[plannerItem.source_id] || [],
                });
            };
            const resources = plannerItems
                .filter((plannerItem) => !plannerItem.parent_id)
                .map((plannerItem) => mapResource(plannerItem));
            let index = 0;
            const addIndex = (mappedResource) => {
                index += 1;
                mappedResource.index = index;
                mappedResource.children.forEach((mappedChildResource) => addIndex(mappedChildResource));
            };
            resources.forEach((resource) => addIndex(resource));
            const sourceIdToPlannerItemIds = {};
            for (const plannerItem of plannerItems) {
                sourceIdToPlannerItemIds[plannerItem.source_id] = plannerItem._id;
            }
            const plannedEventsById = plannedEvents.reduce((acc, plannedEvent) => {
                acc[plannedEvent._id] = plannedEvent;
                return acc;
            }, {});
            const plannedSchedulerEvents = plannedEvents.map((plannedEvent) => ({
                id: plannedEvent._id,
                type: "planned",
                startDate: plannedEvent.start,
                endDate: plannedEvent.end,
                resourceId: plannedEvent.planner_item_id,
            }));
            const actualSchedulerEvents = actualEvents.map((actualEvent) => ({
                id: actualEvent._id,
                type: "actual",
                startDate: actualEvent.start,
                endDate: actualEvent.end || now,
                active: !actualEvent.end,
                resourceId: sourceIdToPlannerItemIds[actualEvent.source_id],
            }));
            const events = [...plannedSchedulerEvents, ...actualSchedulerEvents];
            const dependencies = plannedEvents.flatMap((plannedEvent) => plannedEvent.predecessor_ids.map((predecessorId) => {
                const predecessorEvent = plannedEventsById[predecessorId];
                if (!predecessorEvent) {
                    throw new Error("Missing predecessor event");
                }
                return {
                    from: predecessorId,
                    to: plannedEvent._id,
                    fromSide: "end",
                    toSide: "top",
                    lag: differenceInMinutes(plannedEvent.start, predecessorEvent.end),
                    lagUnit: "m",
                };
            }));
            return {
                resources,
                events,
                dependencies,
            };
        },
        createPlanConfigFromSchedulerData(schedulerPro) {
            const allResources = schedulerPro.resourceStore.allRecords;
            const allEvents = schedulerPro.eventStore.allRecords;
            const allDependencies = schedulerPro.dependencyStore.allRecords;
            const hasEventsOrHasChildrenWithEvents = createHasEventsOrHasChildrenWithEvents(allEvents);
            const planner_items = allResources
                .filter((resourceItem) => hasEventsOrHasChildrenWithEvents(resourceItem))
                .map((resourceItem) => ({
                _id: resourceItem.getData("id"),
                source_id: resourceItem.getData("sourceId"),
                name: resourceItem.getData("name"),
                parent_id: resourceItem.parentId,
                tracking_enabled: resourceItem.getData("trackingEnabled"),
                index: resourceItem.getData("index"),
            }));
            const resourceIdToSourceIds = allResources.reduce((acc, resourceItem) => {
                acc[resourceItem.id] = resourceItem.getData("sourceId");
                return acc;
            }, {});
            const eventIdsByTo = allDependencies.reduce((acc, dependency) => {
                const toEvent = dependency.toEvent;
                const fromEvent = dependency.fromEvent;
                if (!toEvent || !fromEvent) {
                    return acc;
                }
                if (acc[toEvent.id]) {
                    acc[toEvent.id].push(fromEvent.id);
                }
                else {
                    acc[toEvent.id] = [fromEvent.id];
                }
                return acc;
            }, {});
            const planned_events = allEvents
                .filter((eventItem) => eventItem.getData("type") === "planned")
                .map((eventItem) => {
                var _a;
                return ({
                    _id: eventItem.getData("id"),
                    planner_item_id: eventItem.getData("resourceId"),
                    predecessor_ids: ((_a = eventIdsByTo[eventItem.id]) === null || _a === void 0 ? void 0 : _a.map((eventId) => eventId)) || [],
                    start: eventItem.getData("startDate"),
                    end: eventItem.getData("endDate"),
                });
            });
            const actual_events = allEvents
                .filter((eventItem) => eventItem.getData("type") === "actual")
                .map((eventItem) => ({
                _id: eventItem.getData("id"),
                source_id: resourceIdToSourceIds[eventItem.getData("resourceId")],
                start: eventItem.getData("startDate"),
                end: eventItem.getData("active") ? null : eventItem.getData("endDate"),
            }));
            const planner_comments = allResources.flatMap((resourceItem) => resourceItem.getData("comments"));
            return {
                planner_items,
                planned_events,
                actual_events,
                planner_comments,
            };
        },
        getActualEventChanges(schedulerPro) {
            const { changes } = schedulerPro.eventStore;
            if (!changes) {
                return null;
            }
            const { allRecords: allResources } = schedulerPro.resourceStore;
            const resourceIdToSourceIds = {};
            for (const resource of allResources) {
                resourceIdToSourceIds[resource.id] = resource.getData("sourceId");
            }
            const added = changes.added
                .filter((eventItem) => eventItem.getData("type") === "actual")
                .map((eventItem) => ({
                _id: eventItem.getData("id"),
                source_id: resourceIdToSourceIds[eventItem.getData("resourceId")],
                start: eventItem.getData("startDate"),
                end: eventItem.getData("active") ? null : eventItem.getData("endDate"),
            }));
            const modified = changes.modified
                .filter((eventItem) => eventItem.getData("type") === "actual")
                .map((eventItem) => ({
                _id: eventItem.getData("id"),
                start: eventItem.getData("startDate"),
                end: eventItem.getData("active") ? null : eventItem.getData("endDate"),
            }));
            const removed = changes.removed
                .filter((eventItem) => eventItem.getData("type") === "actual")
                .map((event) => ({
                _id: event.getData("id"),
            }));
            return {
                added,
                modified,
                removed,
            };
        },
        resetUndoRedo(schedulerPro) {
            schedulerPro.project.stm.disable();
            schedulerPro.project.stm.resetQueue();
            schedulerPro.project.stm.enable();
        },
        revertAllChanges(schedulerPro) {
            schedulerPro.eventStore.revertChanges();
            schedulerPro.resourceStore.revertChanges();
            schedulerPro.dependencyStore.revertChanges();
            this.resetUndoRedo(schedulerPro);
        },
        removeOrphanedActualEvents(schedulerPro) {
            const { eventStore } = schedulerPro;
            const orphanedActualEvents = eventStore.allRecords.filter((event) => event.getData("type") === "actual" && !event.resourceId);
            eventStore.remove(orphanedActualEvents);
        },
        getInitialPlanConfig(name, customer_name, site_id) {
            const plan = {
                _id: uuidv4(),
                name,
                customer_name,
                site_id,
            };
            const plannerItem = {
                _id: uuidv4(),
                plan_id: plan._id,
                source_id: `oculai-${uuidv4()}`,
                customer_name,
                site_id,
                name: "Planner Item 1",
                parent_id: null,
                tracking_enabled: true,
                index: 1,
                order: 1,
            };
            const plannedEvent = {
                _id: uuidv4(),
                plan_id: plan._id,
                customer_name,
                site_id,
                planner_item_id: plannerItem._id,
                predecessor_ids: [],
                start: add(new Date(), { days: -14 }),
                end: new Date(),
            };
            return {
                plan,
                planner_items: [plannerItem],
                planned_events: [plannedEvent],
                actual_events: [],
                planner_comments: [],
            };
        },
        resetTimespan(schedulerPro) {
            return __awaiter(this, void 0, void 0, function* () {
                const timeSpan = schedulerPro.eventStore.getTotalTimeSpan();
                yield schedulerPro.setTimeSpan(addMonths((timeSpan === null || timeSpan === void 0 ? void 0 : timeSpan.startDate) || new Date(), -3), addMonths((timeSpan === null || timeSpan === void 0 ? void 0 : timeSpan.endDate) || new Date(), 3));
            });
        },
        resetZoomLevel(schedulerPro, defaultPresetName) {
            const presetIndex = [...schedulerPro.presets].findIndex((preset) => preset.id === defaultPresetName);
            if (presetIndex === -1) {
                throw new Error(`Preset ${defaultPresetName} not found`);
            }
            schedulerPro.zoomToLevel(presetIndex);
        },
        resetView(schedulerPro, defaultPresetName) {
            return __awaiter(this, void 0, void 0, function* () {
                if (defaultPresetName) {
                    this.resetZoomLevel(schedulerPro, defaultPresetName);
                }
                const actualEvents = schedulerPro.eventStore.allRecords.filter((event) => event.getData("type") === "actual");
                const endDateInMsEpoch = actualEvents.length > 0
                    ? Math.max(...actualEvents.map((event) => event.getData("endDate").getTime()))
                    : new Date();
                yield this.resetTimespan(schedulerPro);
                yield schedulerPro.scrollToDate(new Date(endDateInMsEpoch), { block: "center" });
                if (schedulerPro.scrollToTop) {
                    yield schedulerPro.scrollToTop();
                }
            });
        },
        restoreView(schedulerPro, presetIndex, scrollState) {
            return __awaiter(this, void 0, void 0, function* () {
                schedulerPro.zoomToLevel(presetIndex);
                yield this.resetTimespan(schedulerPro);
                schedulerPro.restoreScroll(scrollState);
            });
        },
        setProjectStartEndTimeFlag(schedulerPro, includeEnd, t) {
            const { timeRangeStore } = schedulerPro.project;
            const records = schedulerPro.eventStore.allRecords.filter((event) => event.getData("type") === "actual");
            const startRangeId = 2;
            const endRangeId = 3;
            const minTime = Math.min(...records.map((event) => event.getData("startDate").getTime()));
            const maxTime = Math.max(...records.map((event) => event.getData("endDate").getTime()));
            const updateRange = (rangeId, namePrefix, time) => {
                if (!isFinite(time)) {
                    return;
                }
                const range = timeRangeStore.getById(rangeId);
                const rangeDate = new Date(time);
                const name = `${namePrefix} ${format(rangeDate, "dd.MM.yyyy")}`;
                if (range) {
                    range.set("name", name);
                    range.set("startDate", rangeDate);
                }
                else {
                    timeRangeStore.add({ id: rangeId, name, startDate: rangeDate });
                }
            };
            updateRange(startRangeId, t ? t("time.start_gantt") : "START", minTime);
            if (includeEnd) {
                updateRange(endRangeId, t ? t("time.end_gantt") : "END", maxTime);
            }
            else {
                timeRangeStore.remove(endRangeId);
            }
        },
        createProcessEventsForResource(schedulerPro, resourceId, processes) {
            const sortedProcesses = processes
                .slice()
                .sort((a, b) => (encodedLabelsToProcessElement[a.encoded_label] || "").localeCompare(encodedLabelsToProcessElement[b.encoded_label]) || a.start_time.getTime() - b.start_time.getTime());
            const mergedProcesses = sortedProcesses.reduce((acc, process) => {
                const processElement = encodedLabelsToProcessElement[process.encoded_label];
                if (!processElement) {
                    return acc;
                }
                const lastGroup = acc[acc.length - 1];
                if (lastGroup &&
                    lastGroup.date === process.date &&
                    encodedLabelsToProcessElement[lastGroup.processes[0].encoded_label] === processElement &&
                    process.start_time.getTime() <= lastGroup.endTime.getTime()) {
                    if (process.end_time.getTime() > lastGroup.endTime.getTime()) {
                        lastGroup.endTime = process.end_time;
                    }
                    lastGroup.processes.push(process);
                }
                else {
                    acc.push({
                        startTime: process.start_time,
                        endTime: process.end_time,
                        date: process.date,
                        processes: [process],
                        processElement,
                    });
                }
                return acc;
            }, []);
            return mergedProcesses.map((mergedProcess) => ({
                id: mergedProcess.processes[0]._id,
                type: `process_${mergedProcess.processElement}`,
                startDate: mergedProcess.startTime,
                endDate: mergedProcess.endTime,
                resourceId,
                processes: mergedProcess.processes,
                processElement: mergedProcess.processElement,
            }));
        },
        toggleProcessEventsForResource(schedulerPro, resource, allProcesses, showProcesses) {
            const currentProcessEvents = schedulerPro.eventStore
                .getEventsForResource(resource.id)
                .filter((event) => { var _a; return (_a = event.getData("type")) === null || _a === void 0 ? void 0 : _a.startsWith("process_"); });
            const sourceId = resource.getData("sourceId");
            const matchingProcesses = allProcesses.filter((process) => process.planner_item_mapping.source_id === sourceId);
            const processEvents = this.createProcessEventsForResource(schedulerPro, resource.id, matchingProcesses);
            if (processEvents.length > 0 &&
                currentProcessEvents.length === 0 &&
                ((showProcesses !== undefined && showProcesses) || showProcesses === undefined)) {
                schedulerPro.eventStore.applyChangeset({
                    added: processEvents,
                });
                return "added";
            }
            if (currentProcessEvents.length > 0 &&
                ((showProcesses !== undefined && !showProcesses) || showProcesses === undefined)) {
                schedulerPro.eventStore.applyChangeset({
                    removed: currentProcessEvents,
                });
                return "removed";
            }
        },
        getEventStoreChangesWithoutProcesses(schedulerPro) {
            const changes = schedulerPro === null || schedulerPro === void 0 ? void 0 : schedulerPro.eventStore.changes;
            if (!changes) {
                return null;
            }
            const filteredChanges = Object.assign(Object.assign({}, changes), { modified: changes.modified.filter((change) => { var _a; return !((_a = change.getData("type")) === null || _a === void 0 ? void 0 : _a.startsWith("process_")); }), removed: changes.removed.filter((change) => { var _a; return !((_a = change.getData("type")) === null || _a === void 0 ? void 0 : _a.startsWith("process_")); }), added: changes.added.filter((change) => { var _a; return !((_a = change.getData("type")) === null || _a === void 0 ? void 0 : _a.startsWith("process_")); }) });
            if (filteredChanges.added.length === 0 &&
                filteredChanges.modified.length === 0 &&
                filteredChanges.removed.length === 0) {
                return null;
            }
            return filteredChanges;
        },
        getResourceFullName(resource) {
            if (!resource || resource.isRoot) {
                return "";
            }
            return [this.getResourceFullName(resource.parent), resource.name]
                .filter((item) => item)
                .join(" > ");
        },
        scrollToActiveActual(schedulerPro) {
            const plannerResources = schedulerPro.resourceStore.allRecords;
            let firstActiveActual = null;
            for (const item of plannerResources) {
                const actualEventForResource = schedulerPro.eventStore
                    .getEventsForResource(item.getData("id"))
                    .find((event) => event.getData("type") === "actual" && event.getData("active"));
                if (actualEventForResource && item.getData("children").length === 0) {
                    firstActiveActual = actualEventForResource;
                    break;
                }
            }
            if (firstActiveActual) {
                schedulerPro.scrollEventIntoView(firstActiveActual, {
                    block: "center",
                });
            }
        },
    },
};
//# sourceMappingURL=SchedulerProMixins.js.map